import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { Audience, Variant } from '../types'

/**
 * Two-axis flow filter:
 *   - audience: 'unge' | 'aeldre' | 'all'  (URL-prefix segmenting)
 *   - variant:  'chat'  | 'calculator' | 'all'  (UI A/B variant)
 *
 * Sliced independently — every admin endpoint accepts both ?audience= and ?variant=.
 */
export interface FlowSelection {
  audience: Audience | 'all'
  variant: Variant | 'all'
}

interface FlowCtx {
  flow: FlowSelection
  setAudience: (a: Audience | 'all') => void
  setVariant: (v: Variant | 'all') => void

  /** undefined when 'all' (for endpoints with optional ?audience param). */
  audienceParam: Audience | undefined
  /** undefined when 'all' (for endpoints with optional ?variant param). */
  variantParam: Variant | undefined

  /**
   * Backwards-compatible alias for the old single-axis context where the
   * "flow" was just the variant. Any consumer still reading `variant` directly
   * keeps working.
   */
  variant: Variant | 'all'
  audience: Audience | 'all'
}

const Ctx = createContext<FlowCtx | null>(null)
const STORAGE_AUDIENCE = 'lf_admin_audience'
const STORAGE_VARIANT = 'lf_admin_variant'
const STORAGE_LEGACY = 'lf_admin_flow' // legacy single-axis key

function readAudience(): Audience | 'all' {
  try {
    const v = sessionStorage.getItem(STORAGE_AUDIENCE)
    if (v === 'unge' || v === 'aeldre' || v === 'all') return v
  } catch { /* ignore */ }
  return 'all'
}

function readVariant(): Variant | 'all' {
  try {
    const v = sessionStorage.getItem(STORAGE_VARIANT)
    if (v === 'chat' || v === 'calculator' || v === 'all') return v
    // Migrate legacy single-axis key
    const legacy = sessionStorage.getItem(STORAGE_LEGACY)
    if (legacy === 'chat' || legacy === 'calculator' || legacy === 'all') return legacy
  } catch { /* ignore */ }
  return 'all'
}

export function FlowFilterProvider({ children }: { children: ReactNode }) {
  const [audience, setAudienceState] = useState<Audience | 'all'>(readAudience)
  const [variant, setVariantState] = useState<Variant | 'all'>(readVariant)

  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_AUDIENCE, audience) } catch { /* ignore */ }
  }, [audience])
  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_VARIANT, variant) } catch { /* ignore */ }
  }, [variant])

  const value: FlowCtx = {
    flow: { audience, variant },
    setAudience: setAudienceState,
    setVariant: setVariantState,
    audienceParam: audience === 'all' ? undefined : audience,
    variantParam: variant === 'all' ? undefined : variant,
    variant,
    audience,
  }
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useFlowFilter(): FlowCtx {
  const v = useContext(Ctx)
  if (!v) throw new Error('useFlowFilter must be used inside FlowFilterProvider')
  return v
}
